import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

function CardSpinner() {
    return (
        <div style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, borderRadius: "inherit",
        }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem", color: "#6c757d" }} />
        </div>
    );
}

function UpcomingMaintenance({ maintenanceSchedules, loading }) {
    const [collapsed, setCollapsed] = useState(false);
    const items = useMemo(() => {
        if (!maintenanceSchedules?.length) return [];

        return maintenanceSchedules
            .map((ms) => {
                let attrs = ms.schedule_attributes;
                if (typeof attrs === "string") {
                    try { attrs = JSON.parse(attrs); } catch { attrs = {}; }
                }
                const fromDate = attrs?.from ? moment(attrs.from) : null;
                const toDate = attrs?.to ? moment(attrs.to) : fromDate;
                return { ...ms, fromDate, toDate };
            })
            .filter((ms) => ms.fromDate)
            .sort((a, b) => a.fromDate - b.fromDate)
            .slice(0, 7);
    }, [maintenanceSchedules]);

    const daysUntil = (date) => {
        const d = moment(date).startOf("day").diff(moment().startOf("day"), "days");
        return d;
    };

    const badgeFor = (days) => {
        if (days < 0) return { color: "#dc3545", label: `${Math.abs(days)}d overdue` };
        if (days === 0) return { color: "#dc3545", label: "Today" };
        if (days <= 3) return { color: "#fd7e14", label: `${days}d` };
        if (days <= 7) return { color: "#ffc107", label: `${days}d` };
        return { color: "#28a745", label: `${days}d` };
    };

    return (
        <div className={`card card-outline card-warning dashboard-monitor-card ${collapsed ? "collapsed-card" : ""}`} style={{ position: "relative" }}>
            {loading && <CardSpinner />}
            <div className="card-header py-2">
                <h3 className="card-title">
                    <i className="fa fa-cogs mr-2" />
                    Upcoming Maintenance
                </h3>
                <div className="card-tools">
                    <Link to="/maintenance" className="btn btn-tool btn-sm text-muted">
                        <i className="fa fa-external-link" />
                    </Link>
                    <button type="button" className="btn btn-tool" onClick={() => setCollapsed(!collapsed)}>
                        <i className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`} />
                    </button>
                </div>
            </div>
            <div className={`card-body p-0 ${collapsed ? "d-none" : ""}`} style={{ overflowY: "auto" }}>
                {items.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                            style={{ width: 50, height: 50, background: "rgba(255,193,7,0.12)" }}
                        >
                            <i className="fa fa-cogs fa-lg text-warning" />
                        </div>
                        <p className="text-muted small mb-0">No scheduled maintenance on record.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column" style={{ gap: 8, padding: "10px 12px" }}>
                        {items.map((ms) => {
                            const days = daysUntil(ms.fromDate);
                            const badge = badgeFor(days);
                            const isOverdue = days < 0;
                            return (
                                <div
                                    key={ms.id}
                                    className="d-flex align-items-center justify-content-between"
                                    style={{
                                        borderLeft: `3px solid ${badge.color}`,
                                        borderRadius: 6,
                                        background: isOverdue ? "rgba(220,53,69,0.05)" : "#fafafa",
                                        border: `1px solid ${isOverdue ? "rgba(220,53,69,0.18)" : "#e9ecef"}`,
                                        borderLeftWidth: 3,
                                        borderLeftColor: badge.color,
                                        padding: "8px 12px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                    }}
                                >
                                    <div style={{ overflow: "hidden", minWidth: 0 }}>
                                        <div
                                            className="font-weight-bold text-truncate"
                                            style={{ fontSize: "0.82rem" }}
                                            title={ms.name}
                                        >
                                            {isOverdue && <i className="fa fa-exclamation-circle text-danger mr-1" />}
                                            {ms.name}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                            {ms.fromDate.format("MMM D")}
                                            {ms.toDate && !ms.fromDate.isSame(ms.toDate, "day") && ` – ${ms.toDate.format("MMM D")}`}
                                        </div>
                                    </div>
                                    <span
                                        className="badge ml-2 flex-shrink-0"
                                        style={{ background: badge.color, color: "#fff", fontSize: "0.72rem", minWidth: 40, textAlign: "center", borderRadius: 4 }}
                                    >
                                        {badge.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className={`card-footer py-1 ${collapsed ? "d-none" : ""}`}>
                <div className="starek-card-footer">
                    <Link to="/maintenance" className="starek-card-footer-link">
                        View all maintenance schedules <i className="fa fa-arrow-right" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default UpcomingMaintenance;
